import { translateText } from '@alexis/helpers/translation';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import { Outlet, useNavigate, useLocation, useSearchParams } from 'react-router-dom';

import { CAR_RENTAL_ALLOWED_LOCALES } from '@constants/general';

import { changeLocale } from '@redux/actions/localeActions';

import type { Translations } from '@wegoTypes/translations';

interface CarRentalPathRouteProps {
  locale: string;
  localeParam?: string;
  translations: Translations;
}

const CarRentalPathRoute: React.FC<CarRentalPathRouteProps> = ({
  locale,
  localeParam,
  translations,
}): JSX.Element => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  // Check if there's a ulang parameter and redirect if it's not supported
  useEffect(() => {
    const ulangParam = searchParams.get('ulang');

    // If ulang is specified and it's not 'en' or 'ar', redirect to English
    if (ulangParam && !CAR_RENTAL_ALLOWED_LOCALES.includes(ulangParam)) {
      // Create new search params without the unsupported ulang
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('ulang', 'en'); // Set to English as fallback

      // Update Redux store
      dispatch(changeLocale('en'));

      // Redirect to the same path but with English as the language
      navigate(
        {
          pathname: location.pathname,
          search: newSearchParams.toString(),
        },
        { replace: true },
      );
    }

    if (!localeParam && !ulangParam && !CAR_RENTAL_ALLOWED_LOCALES.includes(locale)) {
      // Update Redux store
      dispatch(changeLocale('en'));

      const currentPath = location.pathname;
      const englishPath = currentPath.startsWith('/') ? `/en${currentPath}` : `/en/${currentPath}`;

      navigate(
        {
          pathname: englishPath,
          search: location.search,
        },
        { replace: true },
      );
    }
  }, [location.pathname, searchParams, locale, localeParam, navigate, dispatch]);

  const getHostNameWithoutWWW = useCallback<(hostname: string) => string>((hostname: string) => {
    const hostNameWithoutWWW = hostname.replace('www.', '');

    return hostNameWithoutWWW.replace(/^./, hostNameWithoutWWW[0].toUpperCase());
  }, []);

  const title = useMemo<string>(() => {
    return translateText(
      translations.car_rental_meta_title,
      locale || 'en',
      new Date().getFullYear(),
      getHostNameWithoutWWW(window.location.hostname),
    );
  }, [locale, translations, getHostNameWithoutWWW]);

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name='description' content={String(translations.car_rental_meta_description)} />
        <link rel='canonical' href={`${window.location.origin}${window.location.pathname}`} />
      </Helmet>

      <Outlet />
    </>
  );
};

export default CarRentalPathRoute;
